import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { accountApi } from '../../services/accountApi';

const METHODS = [
  { key: 'visa_4242', label: 'Visa •••• 4242' },
  { key: 'mastercard_4444', label: 'Mastercard •••• 4444' },
  { key: 'payment_failure', label: 'Payment failure demo' },
  { key: 'past_due', label: 'Past-due demo' },
];

function MockCheckoutPage() {
  const { activePartnerId } = useAuth();
  const [searchParams] = useSearchParams();
  const tier = searchParams.get('tier') || 'pro';
  const interval = searchParams.get('interval') || 'monthly';
  const navigate = useNavigate();
  const [method, setMethod] = useState('visa_4242');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    if (!activePartnerId) return;
    accountApi.getPartnerBilling(activePartnerId).then(setOverview).catch(() => {});
  }, [activePartnerId]);

  const priceLabel = useMemo(() => {
    if (interval === 'annual') return '$490.00 (example annual)';
    return '$49.00 (example monthly)';
  }, [interval]);

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      // Ensure pending checkout state
      await accountApi.startBillingCheckout(activePartnerId, { tier, interval });
      const res = await accountApi.completeMockCheckout(activePartnerId, {
        payment_method_key: method,
      });
      setDone(res);
    } catch (e) {
      setError(e.message || 'Mock checkout failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!activePartnerId) {
    return <Alert variant="warning">Select a Partner workspace first.</Alert>;
  }

  if (done?.result === 'mock_active') {
    return (
      <div className="console-page" style={{ maxWidth: 560 }}>
        <Alert variant="success">
          <strong>Mock checkout complete.</strong> No real payment was processed.
        </Alert>
        <p>Subscription is now mock-active for Pro.</p>
        <Button onClick={() => navigate('/console/account/billing')}>
          Return to Account &amp; Billing
        </Button>
      </div>
    );
  }

  return (
    <div className="console-page" style={{ maxWidth: 560 }}>
      <Alert variant="warning" className="mb-3">
        <strong>Test mode — Mock checkout</strong>
        <div className="small">Stripe is not connected. No real charges occur.</div>
      </Alert>
      <h1 className="h4">Mock Checkout</h1>
      <Card className="mb-3">
        <Card.Body>
          <p className="mb-1">
            <strong>Partner:</strong> {overview?.partner?.name || '—'}
          </p>
          <p className="mb-1">
            <strong>Tier:</strong> {tier}
          </p>
          <p className="mb-1">
            <strong>Interval:</strong> {interval}
          </p>
          <p className="mb-1">
            <strong>Subtotal:</strong> {priceLabel}
          </p>
          <p className="mb-1">
            <strong>Tax:</strong> $0.00 (placeholder)
          </p>
          <p className="mb-0">
            <strong>Total:</strong> {priceLabel}
          </p>
          <p className="small text-muted mt-2 mb-0">Example pricing for UI testing.</p>
        </Card.Body>
      </Card>
      <Form.Group className="mb-3">
        <Form.Label>Payment method (mock)</Form.Label>
        <Form.Select value={method} onChange={(e) => setMethod(e.target.value)}>
          {METHODS.map((m) => (
            <option key={m.key} value={m.key}>
              {m.label}
            </option>
          ))}
        </Form.Select>
        <Form.Text>No real card details are collected.</Form.Text>
      </Form.Group>
      {error && <Alert variant="danger">{error}</Alert>}
      <div className="d-flex gap-2">
        <Button disabled={busy} onClick={confirm}>
          {busy ? <Spinner size="sm" /> : 'Confirm mock payment'}
        </Button>
        <Button as={Link} to="/console/account/billing" variant="outline-secondary">
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default MockCheckoutPage;
